import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Gavel, Search, AlertTriangle, FileText, Building2, User, RefreshCw, Loader2,
  Scale, Banknote, Calendar, MessageSquare, ChevronDown, ChevronUp, ArrowRight,
  Target, Clock, Flag, MapPin, Bell, CheckCircle, Pause, AlertCircle, Timer,
  Users, TrendingUp, Filter, MoreVertical, Edit, Send, FileCheck
} from "lucide-react";
import { 
  getLegalCasesV2, getLegalCaseHandlers, assignCaseHandler, updateCaseStage,
  updateCaseActionStatus, addCaseNote, getUpcomingActions, getLegalCaseLoanDiskData,
  getXeroTruePrincipal, getXeroStatus
} from "@/lib/api";
import { toast } from "sonner";

// SimplyFunded brand color
const SF_BLUE = "#3B70E0";

// Action status colors and icons
const ACTION_STATUS_CONFIG = {
  action_required: { label: "Action Required", color: "bg-red-500", textColor: "text-red-600", bgLight: "bg-red-50", icon: AlertCircle },
  waiting: { label: "Waiting", color: "bg-yellow-500", textColor: "text-yellow-600", bgLight: "bg-yellow-50", icon: Clock },
  chase_required: { label: "Chase Required", color: "bg-orange-500", textColor: "text-orange-600", bgLight: "bg-orange-50", icon: Bell },
  on_hold: { label: "On Hold", color: "bg-gray-400", textColor: "text-gray-600", bgLight: "bg-gray-50", icon: Pause },
  complete: { label: "Complete", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50", icon: CheckCircle },
};

// Legal stages for dropdown
const LEGAL_STAGES_OPTIONS = [
  { value: "dn_lba", label: "DN/LBA", jurisdiction: "all" },
  { value: "sd2_served", label: "SD2 Served", jurisdiction: "england_wales" },
  { value: "sd2_substituted", label: "SD2 - Substituted Service", jurisdiction: "england_wales" },
  { value: "form5_served", label: "Form 5 Served (Scotland)", jurisdiction: "scotland" },
  { value: "awaiting_challenge", label: "Awaiting Challenge Period", jurisdiction: "england_wales" },
  { value: "awaiting_challenge_scot", label: "Awaiting Challenge (Scotland)", jurisdiction: "scotland" },
  { value: "can_petition", label: "Can File Petition", jurisdiction: "england_wales" },
  { value: "can_sequestrate", label: "Can Apply for Sequestration", jurisdiction: "scotland" },
  { value: "petition_filed", label: "Petition Filed", jurisdiction: "england_wales" },
  { value: "sequestration_applied", label: "Sequestration Applied", jurisdiction: "scotland" },
  { value: "hearing_scheduled", label: "Hearing Scheduled", jurisdiction: "england_wales" },
  { value: "bankruptcy_order", label: "Bankruptcy Order Made", jurisdiction: "england_wales" },
  { value: "award_of_sequestration", label: "Award of Sequestration", jurisdiction: "scotland" },
  { value: "ccj", label: "CCJ Obtained", jurisdiction: "england_wales" },
  { value: "charging_order_interim", label: "Interim Charging Order", jurisdiction: "england_wales" },
  { value: "charging_order_final", label: "Final Charging Order", jurisdiction: "england_wales" },
  { value: "winding_up", label: "Winding-up Petition", jurisdiction: "all" },
  { value: "iva", label: "IVA Proposed/Accepted", jurisdiction: "all" },
  { value: "payment_plan", label: "Payment Plan Agreed", jurisdiction: "all" },
  { value: "collections_restructured", label: "Back to Collections", jurisdiction: "all" },
  { value: "write_off", label: "Written Off", jurisdiction: "all" },
  { value: "fully_paid", label: "Fully Paid", jurisdiction: "all" },
];

export default function LegalCaseTracker() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [jurisdictionFilter, setJurisdictionFilter] = useState("all");
  const [handlerFilter, setHandlerFilter] = useState("all");
  const [expandedCard, setExpandedCard] = useState(null);
  const [cardData, setCardData] = useState({});
  const [caseHandlers, setCaseHandlers] = useState([]);
  const [upcomingActions, setUpcomingActions] = useState([]);
  const [activeTab, setActiveTab] = useState("cases");
  const [xeroConnected, setXeroConnected] = useState(false);
  
  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [dialogNote, setDialogNote] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedHandler, setSelectedHandler] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (actionFilter !== "all") filters.actionStatus = actionFilter;
      if (jurisdictionFilter !== "all") filters.jurisdiction = jurisdictionFilter;
      if (handlerFilter !== "all") filters.caseHandlerId = handlerFilter;
      
      const [casesData, handlersData, upcomingData, xeroStatus] = await Promise.all([
        getLegalCasesV2(filters),
        getLegalCaseHandlers(),
        getUpcomingActions(7),
        getXeroStatus().catch(() => ({ connected: false }))
      ]);
      
      setCases(casesData.cases || []);
      setSummary(casesData.summary || {});
      setCaseHandlers(handlersData.case_handlers || []);
      setUpcomingActions(upcomingData.upcoming_actions || []);
      setXeroConnected(xeroStatus.connected && !xeroStatus.expired);
    } catch (error) {
      toast.error("Failed to load legal cases");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, actionFilter, jurisdictionFilter, handlerFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadCardData = async (caseItem) => {
    const caseId = caseItem.id;
    if (cardData[caseId]?.loaded) return;

    setCardData(prev => ({ ...prev, [caseId]: { ...prev[caseId], loading: true } }));

    try {
      const ldData = await getLegalCaseLoanDiskData(caseId);
      let xeroData = null;
      if (xeroConnected && caseItem.sf_number) {
        try {
          xeroData = await getXeroTruePrincipal(caseItem.sf_number);
        } catch (e) {
          console.log("Xero data not available");
        }
      }

      setCardData(prev => ({
        ...prev,
        [caseId]: { loaded: true, loading: false, loandisk: ldData, xero: xeroData }
      }));
    } catch (error) {
      setCardData(prev => ({ ...prev, [caseId]: { loaded: true, loading: false, error: true } }));
    }
  };

  const handleExpandCard = (caseItem) => {
    if (expandedCard === caseItem.id) {
      setExpandedCard(null);
    } else {
      setExpandedCard(caseItem.id);
      loadCardData(caseItem);
    }
  };

  const handleAssignHandler = async () => {
    if (!selectedCase || !selectedHandler) return;
    try {
      const handler = caseHandlers.find(h => h.id === selectedHandler);
      await assignCaseHandler(selectedCase.id, selectedHandler, handler?.name || "Unknown");
      toast.success(`Case assigned to ${handler?.name}`);
      setAssignDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error("Failed to assign handler");
    }
  };

  const handleUpdateStage = async () => {
    if (!selectedCase || !selectedStage) return;
    try {
      const result = await updateCaseStage(selectedCase.id, selectedStage, dialogNote || null);
      toast.success(`Stage updated. ${result.deadlines_created?.length || 0} deadlines created.`);
      setStageDialogOpen(false);
      setDialogNote("");
      loadData();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedCase || !selectedStatus) return;
    try {
      await updateCaseActionStatus(selectedCase.id, selectedStatus, dialogNote || null);
      toast.success("Action status updated");
      setStatusDialogOpen(false);
      setDialogNote("");
      loadData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAddNote = async () => {
    if (!selectedCase || !dialogNote.trim()) return;
    try {
      await addCaseNote(selectedCase.id, dialogNote);
      toast.success("Note added");
      setNoteDialogOpen(false);
      setDialogNote("");
      loadData();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "£0";
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusConfig = (status) => ACTION_STATUS_CONFIG[status] || ACTION_STATUS_CONFIG.action_required;

  const filteredStages = (jurisdiction) => {
    return LEGAL_STAGES_OPTIONS.filter(s => s.jurisdiction === "all" || s.jurisdiction === jurisdiction);
  };

  // Sort cases by balance descending
  const sortedCases = [...cases].sort((a, b) => (b.balance || 0) - (a.balance || 0));

  const CaseRow = ({ caseItem, index }) => {
    const isExpanded = expandedCard === caseItem.id;
    const data = cardData[caseItem.id] || {};
    const ld = data.loandisk?.loandisk_data;
    const xero = data.xero;
    const statusConfig = getStatusConfig(caseItem.action_status);
    const StatusIcon = statusConfig.icon;

    return (
      <Card 
        className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'}`}
        data-testid={`case-row-${caseItem.sf_number}`}
      >
        {/* Main Row */}
        <div className="p-4 cursor-pointer" onClick={() => handleExpandCard(caseItem)}>
          <div className="flex items-center gap-4">
            {/* Status Indicator */}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${statusConfig.bgLight}`}>
              <StatusIcon className={`w-5 h-5 ${statusConfig.textColor}`} />
            </div>

            {/* Company Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base truncate">{caseItem.company_name || "Unknown"}</h3>
                <Badge className={`${statusConfig.color} text-white text-xs`}>
                  {statusConfig.label}
                </Badge>
                {caseItem.jurisdiction === "scotland" && (
                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                    <MapPin className="w-3 h-3 mr-1" /> Scotland
                  </Badge>
                )}
                {caseItem.legal_stage_v2 && (
                  <Badge variant="outline" className="text-xs">
                    {LEGAL_STAGES_OPTIONS.find(s => s.value === caseItem.legal_stage_v2)?.label || caseItem.legal_stage_v2}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">{caseItem.sf_number}</span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {caseItem.director || "No director"}
                </span>
                {caseItem.case_handler_name && (
                  <span className="flex items-center gap-1 text-blue-600">
                    <Users className="w-3 h-3" />
                    {caseItem.case_handler_name}
                  </span>
                )}
              </div>
            </div>

            {/* Next Deadline */}
            {caseItem.next_deadline && (
              <div className="text-right flex-shrink-0 px-3 py-1 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-600 font-medium">{caseItem.next_deadline.label}</p>
                <p className="text-sm font-semibold text-amber-700">{formatDate(caseItem.next_deadline.date)}</p>
              </div>
            )}

            {/* Balance */}
            <div className="text-right flex-shrink-0">
              <p className="text-xl font-bold text-red-600">{formatCurrency(caseItem.balance)}</p>
              <p className="text-xs text-muted-foreground">Balance</p>
            </div>

            {/* Expand Icon */}
            <div className="flex-shrink-0">
              {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t bg-slate-50/50">
            <div className="p-4 space-y-4">
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedCase(caseItem); setAssignDialogOpen(true); }}>
                  <Users className="w-3 h-3 mr-1" /> Assign Handler
                </Button>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedCase(caseItem); setSelectedStage(caseItem.legal_stage_v2 || ""); setStageDialogOpen(true); }}>
                  <TrendingUp className="w-3 h-3 mr-1" /> Update Stage
                </Button>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedCase(caseItem); setSelectedStatus(caseItem.action_status || ""); setStatusDialogOpen(true); }}>
                  <Flag className="w-3 h-3 mr-1" /> Set Status
                </Button>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedCase(caseItem); setNoteDialogOpen(true); }}>
                  <MessageSquare className="w-3 h-3 mr-1" /> Add Note
                </Button>
                <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/legal/case/${caseItem.id}`); }}>
                  <ArrowRight className="w-3 h-3 mr-1" /> Full Case File
                </Button>
              </div>

              {/* Case Notes - Instant from Airtable */}
              <div className="p-3 bg-white rounded-lg border border-amber-200 bg-amber-50/30">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-semibold uppercase">Case Notes</span>
                </div>
                <p className="text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {caseItem.case_notes || "No notes recorded."}
                </p>
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-xs text-muted-foreground mb-1">Next Action</div>
                  <p className="text-sm font-medium">{caseItem.next_action || "—"}</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-xs text-muted-foreground mb-1">PDN Date</div>
                  <p className="text-sm font-medium">{formatDate(caseItem.pdn_date)}</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-xs text-muted-foreground mb-1">DN Date</div>
                  <p className="text-sm font-medium">{formatDate(caseItem.dn_date)}</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <div className="text-xs text-muted-foreground mb-1">LBA Date</div>
                  <p className="text-sm font-medium">{formatDate(caseItem.lba_date)}</p>
                </div>
              </div>

              {/* External Data - LoanDisk / Xero */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">External Data (LoanDisk / Xero)</span>
                  {data.loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
                
                {data.loading ? (
                  <div className="grid grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="p-3 bg-white rounded-lg border animate-pulse">
                        <div className="h-3 w-20 bg-slate-200 rounded mb-2"></div>
                        <div className="h-6 w-16 bg-slate-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 text-blue-600 mb-1">
                        <Scale className="w-4 h-4" />
                        <span className="text-xs font-medium">LoanDisk Balance</span>
                      </div>
                      <p className="text-xl font-bold">{ld ? formatCurrency(ld.loan_balance) : "—"}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 text-emerald-600 mb-1">
                        <Banknote className="w-4 h-4" />
                        <span className="text-xs font-medium">Principal at Risk</span>
                      </div>
                      <p className="text-xl font-bold text-amber-600">
                        {xero?.found ? formatCurrency(xero.true_principal?.principal_at_risk) : "—"}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 text-red-600 mb-1">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs font-medium">Arrears</span>
                      </div>
                      <p className="text-xl font-bold text-red-600">{ld ? formatCurrency(ld.arrears_amount) : "—"}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Upcoming Deadlines */}
              {caseItem.deadlines && caseItem.deadlines.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Timer className="w-4 h-4 text-orange-600" />
                    <span className="text-xs font-medium text-muted-foreground uppercase">Upcoming Deadlines</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {caseItem.deadlines.slice(0, 5).map((dl, idx) => (
                      <div key={idx} className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                        <span className="font-medium text-orange-700">{dl.label}</span>
                        <span className="text-orange-600 ml-2">{formatDate(dl.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (loading && cases.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gavel className="w-6 h-6 text-red-600" />
            Legal Case Tracker
          </h1>
          <p className="text-muted-foreground">Streamlined workflow with auto-deadlines</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md" onClick={() => setActionFilter("action_required")}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Action Required</p>
                <p className="text-2xl font-bold">{summary.action_required || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md" onClick={() => setActionFilter("chase_required")}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Chase Required</p>
                <p className="text-2xl font-bold">{summary.chase_required || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500 cursor-pointer hover:shadow-md" onClick={() => setActionFilter("waiting")}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Waiting</p>
                <p className="text-2xl font-bold">{summary.waiting || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 cursor-pointer hover:shadow-md" onClick={() => setActiveTab("upcoming")}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Due This Week</p>
                <p className="text-2xl font-bold">{summary.upcoming_this_week || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="pt-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Total at Risk</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_balance || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Cases / Upcoming Actions */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="cases">All Cases ({cases.length})</TabsTrigger>
          <TabsTrigger value="upcoming">
            <Bell className="w-4 h-4 mr-1" />
            Upcoming Actions ({upcomingActions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cases" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by company, SF#, or director..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Action Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="action_required">🔴 Action Required</SelectItem>
                <SelectItem value="chase_required">🟠 Chase Required</SelectItem>
                <SelectItem value="waiting">🟡 Waiting</SelectItem>
                <SelectItem value="on_hold">⚪ On Hold</SelectItem>
                <SelectItem value="complete">✅ Complete</SelectItem>
              </SelectContent>
            </Select>
            <Select value={jurisdictionFilter} onValueChange={setJurisdictionFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Jurisdiction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="england_wales">England & Wales</SelectItem>
                <SelectItem value="scotland">Scotland</SelectItem>
              </SelectContent>
            </Select>
            <Select value={handlerFilter} onValueChange={setHandlerFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Case Handler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Handlers</SelectItem>
                {caseHandlers.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Case List */}
          {sortedCases.length === 0 ? (
            <Card className="bg-slate-50">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No cases found matching your criteria</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {sortedCases.map((caseItem, index) => (
                <CaseRow key={caseItem.id} caseItem={caseItem} index={index} />
              ))}
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center">
            Showing {sortedCases.length} cases
          </p>
        </TabsContent>

        <TabsContent value="upcoming" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Upcoming Deadlines (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingActions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No upcoming deadlines</p>
              ) : (
                <div className="space-y-3">
                  {upcomingActions.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:shadow-sm cursor-pointer" onClick={() => navigate(`/legal/case/${item.airtable_id}`)}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.days_until <= 3 ? 'bg-red-100' : 'bg-amber-100'}`}>
                          <span className={`text-sm font-bold ${item.days_until <= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                            {item.days_until}d
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{item.company_name}</p>
                          <p className="text-sm text-muted-foreground">
                            <span className="font-mono">{item.sf_number}</span>
                            {item.case_handler_name && ` • ${item.case_handler_name}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-amber-700">{item.deadline?.label}</p>
                        <p className="text-sm text-muted-foreground">{formatDate(item.deadline_date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">{formatCurrency(item.balance)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Handler Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Case Handler</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Assigning: <strong>{selectedCase?.company_name}</strong> ({selectedCase?.sf_number})
            </p>
            <Select value={selectedHandler} onValueChange={setSelectedHandler}>
              <SelectTrigger>
                <SelectValue placeholder="Select case handler..." />
              </SelectTrigger>
              <SelectContent>
                {caseHandlers.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name} ({h.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignHandler} disabled={!selectedHandler}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Legal Stage</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Updating: <strong>{selectedCase?.company_name}</strong>
              {selectedCase?.jurisdiction === "scotland" && (
                <Badge variant="outline" className="ml-2 text-xs">Scotland</Badge>
              )}
            </p>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger>
                <SelectValue placeholder="Select new stage..." />
              </SelectTrigger>
              <SelectContent>
                {filteredStages(selectedCase?.jurisdiction || "england_wales").map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Add notes (optional)..."
              value={dialogNote}
              onChange={(e) => setDialogNote(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              💡 Deadlines will be auto-calculated based on the stage
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStageDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleUpdateStage} disabled={!selectedStage}>Update Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Action Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Action Status</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Updating: <strong>{selectedCase?.company_name}</strong>
            </p>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="action_required">🔴 Action Required</SelectItem>
                <SelectItem value="waiting">🟡 Waiting</SelectItem>
                <SelectItem value="chase_required">🟠 Chase Required</SelectItem>
                <SelectItem value="on_hold">⚪ On Hold</SelectItem>
                <SelectItem value="complete">✅ Complete</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Add notes (optional)..."
              value={dialogNote}
              onChange={(e) => setDialogNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={!selectedStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Case Note</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Adding note to: <strong>{selectedCase?.company_name}</strong>
            </p>
            <Textarea
              placeholder="Enter your note..."
              value={dialogNote}
              onChange={(e) => setDialogNote(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!dialogNote.trim()}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
